import { BreakDaysType } from '@havenengineering/module-shared-owners-library/dist/enums/breakDaysType';
import { PeakType } from '@havenengineering/module-shared-owners-library/dist/enums/peakType';
import { L2oSummary } from '@havenengineering/module-shared-owners-library/dist/interfaces/l2oSummary';
import { EventData } from '@havenengineering/module-shared-owners-ui/dist/components/EventCalendar';
import { DateTime, Interval } from 'luxon';

import { InfoSelection } from '../../components/EventCalendarApendix/InfoPanel';
import {
  CANCEL_ARRIVAL_ERROR_MESSAGE,
  isLetWithHaven,
  isUnavailable,
  REMOVE_BREAK_ERROR_MESSAGE,
  REMOVE_LET_2_OWN_BELOW_MINIMUM_ERROR_MESSAGE,
  REMOVE_LET_TIME_ERROR_MESSAGE,
} from '../../helpers/calendar';
import {
  fetchWrapper,
  withApiBaseUrl,
  withArrivalsApiBaseUrl,
} from '../../helpers/fetch';
import {
  handleArrivalBookingOnCalendarGTM,
  handleGTMArrivalManagement,
} from '../../helpers/googleTag';
import { ArrivalDataEnum, BreakDataAction } from '../../pages/bookings';
import { BookingType } from '../../types/bookingType';
import { BreakVisitType } from '../../types/lettings';

export const updateBooking = async (
  body: UpdateBookingBody,
  bookingDetails: ArrivalBooking
) => {
  const response = await fetchWrapper(
    withArrivalsApiBaseUrl(
      `/booking/${bookingDetails.shortCode}/scheduled-arrival`
    ),
    {
      method: 'PUT',
      credentials: 'include',
      body: JSON.stringify(body),
    }
  );
  return response;
};

export interface SwappableBreak {
  startDate: string;
  endDate: string;
  status: number;
  value: string;
  packageId: number;
  breakDaysType: BreakDaysType;
  breakPeakType: PeakType;
}

export interface SwappableBreaksResponse {
  [key: string]: SwappableBreak[];
}

export const fetchSwappableBreaks = async (
  accountId: number,
  startDate: string
) =>
  await fetchWrapper<SwappableBreaksResponse | null>(
    withApiBaseUrl(
      `/plot/${accountId}/swappable-breaks?startDate=${startDate}`
    ),
    {
      method: 'GET',
      credentials: 'include',
    }
  );

export const sendBreakSwap = async (
  accountId: number,
  packageId: number,
  newPackageId: number
) =>
  await fetchWrapper<SwappableBreaksResponse>(
    withApiBaseUrl(`/plot/${accountId}/swappable-breaks`),
    {
      method: 'PUT',
      credentials: 'include',
      body: JSON.stringify({
        packageId,
        newPackageId,
      }),
    }
  );

export const resendBookingEmail = async (
  shortCode: string,
  letterEmail: string,
  ownerEmail: string
) =>
  await fetchWrapper(
    withArrivalsApiBaseUrl(`/booking/${shortCode}/user/confirmationEmail`),
    {
      method: 'PUT',
      credentials: 'include',
      body: JSON.stringify({
        letterEmail,
        ownerEmail,
      }),
    }
  );

export const getPrivateGuestEmailConfirmationInfo = async (shortCode: string) =>
  await fetchWrapper(
    withArrivalsApiBaseUrl(
      `/booking/${shortCode}/user/leadGuest?select[]=confirmationEmailResentAt&select[]=email`
    ),
    {
      credentials: 'include',
    }
  );

export const upsertParticipants = async (
  body: UpsertParticipantsBody,
  bookingDetails: ArrivalBooking,
  account: UserAccount | null
): Promise<UpsertParticipantsResponse> => {
  try {
    const response = await fetchWrapper<UpsertParticipantsResponse>(
      withArrivalsApiBaseUrl(
        `/booking/${bookingDetails.shortCode}/user/additionalGuests`
      ),
      {
        method: 'PUT',
        credentials: 'include',
        body: JSON.stringify(body),
      }
    );
    await handleGTMArrivalManagement(
      {
        ...bookingDetails,
        users: response.participants,
        childNumber: response.childNumber,
        toddlersNumber: response.toddlersNumber,
        accountNo: account?.accountNo,
        areaName: account?.areaName,
        pitchNumber: account?.pitchNumber,
        successFlag: 'success',
      },
      'arrivals edit stay save changes'
    );
    return response;
  } catch (error) {
    await handleGTMArrivalManagement(
      {
        ...bookingDetails,
        accountNo: account?.accountNo,
        areaName: account?.areaName,
        pitchNumber: account?.pitchNumber,
        successFlag: 'error',
        errorType: error?.message,
      },
      'arrivals edit stay save changes'
    );
    throw error;
  }
};

export const downloadCarpass = async (
  bookingDetails: ArrivalBooking,
  account: UserAccount | null
) => {
  try {
    window.open(
      withArrivalsApiBaseUrl(`/booking/${bookingDetails.shortCode}/carPass`),
      '_blank'
    );

    await handleGTMArrivalManagement(
      {
        ...bookingDetails,
        accountNo: account?.accountNo,
        areaName: account?.areaName,
        pitchNumber: account?.pitchNumber,
        successFlag: 'success',
      },
      'download car pass'
    );
  } catch (error) {
    await handleGTMArrivalManagement(
      {
        ...bookingDetails,
        accountNo: account?.accountNo,
        areaName: account?.areaName,
        pitchNumber: account?.pitchNumber,
        successFlag: 'error',
        errorType: error?.message,
      },
      'download car pass'
    );
    throw error;
  }
};

const deleteArrivalBooking = async (booking: ArrivalBooking) => {
  const response = {
    success: false,
    error: '',
    info: '',
  };

  try {
    await fetchWrapper(
      withArrivalsApiBaseUrl(`/booking/${booking.shortCode}`),
      {
        method: 'DELETE',
        credentials: 'include',
      }
    );
    response.success = true;

    handleArrivalBookingOnCalendarGTM(booking, 'cancel arrival');
  } catch (error) {
    response.error = CANCEL_ARRIVAL_ERROR_MESSAGE;
  }

  return response;
};

export const deleteArrivalBookingOrHavenLet = async (
  accountId: number,
  eventSelection: EventData,
  allPeakDatesData: PeakDatesData[],
  let2OwnSummary: L2oSummary | undefined,
  { enforceMinimum = true }: { enforceMinimum?: boolean } = {}
): Promise<{ success: boolean; error: string; info: string }> => {
  const response = {
    success: false,
    error: '',
    info: '',
  };

  if (eventSelection.code === ArrivalDataEnum.ARRIVAL_BOOKING) {
    const booking = eventSelection.data as ArrivalBooking;
    return await deleteArrivalBooking(booking);
  }

  if (accountId && eventSelection.code !== ArrivalDataEnum.ARRIVAL_BOOKING) {
    try {
      const code = eventSelection.code;
      const startDate = eventSelection.data.startDate;
      const { outOfSeason, withinSixWeeks } = eventSelection.data.status;

      if (isLetWithHaven(code) && withinSixWeeks) {
        response.info = REMOVE_LET_TIME_ERROR_MESSAGE;
        return response;
      }

      if (
        enforceMinimum &&
        isBreakRemovalDisabled(eventSelection, allPeakDatesData, let2OwnSummary)
      ) {
        response.info = REMOVE_LET_2_OWN_BELOW_MINIMUM_ERROR_MESSAGE;
        return response;
      }

      const transitions: BreakTransition[] = await fetchWrapper(
        withApiBaseUrl(
          `/plot/${accountId}/lettingbreaks/status?status=${code}&startDate=${startDate}&outOfSeason=${outOfSeason}`
        ),
        {
          method: 'GET',
          credentials: 'include',
        }
      );
      if (transitions.length > 0) {
        const removeBreakTransition = transitions.find(
          (tr: BreakTransition) => tr.action === BreakDataAction.REMOVE_LET
        );

        if (!removeBreakTransition) {
          response.error = REMOVE_BREAK_ERROR_MESSAGE;
          return response;
        }
        const success = await performBreakDataChange(accountId, [
          {
            startDate: eventSelection.data.startDate,
            status: removeBreakTransition.code,
          },
        ]);
        if (success) {
          response.success = true;
        } else {
          response.error = REMOVE_BREAK_ERROR_MESSAGE;
        }
      } else {
        response.error = REMOVE_BREAK_ERROR_MESSAGE;
      }
    } catch (error) {
      response.error = REMOVE_BREAK_ERROR_MESSAGE;
    }
  }

  return response;
};

export const performBreakDataChange = async (
  accountId: number,
  breaks: BreakChange[]
) => {
  if (accountId) {
    try {
      await fetchWrapper(withApiBaseUrl(`/plot/${accountId}/lettingbreaks`), {
        method: 'PUT',
        credentials: 'include',
        body: JSON.stringify({
          breaks: breaks,
        }),
      });
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }
};

export interface FailedBreakUpdate {
  parkLettingPackageId: number;
  errorMessage: string;
  startDate: string;
  duration: number;
}

export interface BreakChangeInput {
  startDate: string;
  status: number;
  capWaitlist?: boolean;
}

// Unlike performBreakDataChange (which swallows the response and returns a
// boolean), this surfaces the PUT response so an on-tick caller can react to a
// per-break failedBreakUpdate. Throws on network/HTTP error.
export const putBreakChange = async (
  accountId: number,
  breaks: BreakChangeInput[]
): Promise<{ failedBreakUpdates?: FailedBreakUpdate[] }> =>
  await fetchWrapper(withApiBaseUrl(`/plot/${accountId}/lettingbreaks`), {
    method: 'PUT',
    credentials: 'include',
    body: JSON.stringify({ breaks }),
  });

export const isBreakRemovalDisabled = (
  eventSelection: EventData,
  allPeakDatesData: PeakDatesData[],
  let2OwnSummary: L2oSummary | undefined
) => {
  const breakDaysType = getBreakDaysType(
    eventSelection.data.startDate,
    eventSelection.data.endDate
  );

  const peakType = getBreakPeakType(
    eventSelection.data.startDate,
    eventSelection.data.endDate,
    allPeakDatesData
  );

  return (
    (isLetWithHaven(eventSelection.code) &&
      peakType &&
      breakDaysType &&
      let2OwnSummary &&
      let2OwnSummary.isLet2Own &&
      let2OwnSummary.summary &&
      let2OwnSummary.summary[peakType][breakDaysType].current - 1 <
        let2OwnSummary.summary[peakType][breakDaysType].min) ||
    false
  );
};

export interface SelectedBreakTransition {
  startDate: string;
  endDate: string;
  label: string;
  parkLettingPackageId?: number;
  status: number;
  originalLabel: string;
  outOfSeason: boolean;
  checked: boolean;
  removeActionStatusCode?: number;
  disabled: boolean;
  daysType: BreakDaysType | null;
  peakType: PeakType | null;
  capWaitlist?: boolean;
}

export const getBreakDaysType = (
  startDate: string,
  endDate: string
): BreakDaysType | null => {
  const startDatetime = DateTime.fromISO(startDate)
    .startOf('day')
    .setZone('Europe/London');
  const endDateTime = DateTime.fromISO(endDate)
    .startOf('day')
    .setZone('Europe/London');

  switch (endDateTime.diff(startDatetime, 'days').days) {
    case 4:
      return BreakDaysType.MIDWEEK;
    case 3:
      return BreakDaysType.WEEKEND;
    default:
      return null;
  }
};

export const getBreakPeakType = (
  startDate: string,
  endDate: string,
  allPeakDatesData: PeakDatesData[]
): PeakType => {
  const peakDate = allPeakDatesData.find(
    (x) => x.startDate === startDate && x.endDate === endDate
  );

  const breakPeakType = peakDate
    ? getPeakTypeFromPeakDate(peakDate)
    : PeakType.OFF_PEAK;

  return breakPeakType!;
};

const getPeakTypeFromPeakDate = (
  parkPeakDate: PeakDatesData | null | undefined
) => {
  if (!parkPeakDate) {
    return null;
  }

  if (parkPeakDate.isSuperPeakDate) {
    return PeakType.SUPER_PEAK;
  } else if (parkPeakDate.isPeakDate) {
    return PeakType.PEAK;
  } else {
    return PeakType.OFF_PEAK;
  }
};

export enum PeakTypeMapping {
  'offPeak' = 'OFF-PEAK',
  'peak' = 'PEAK',
  'superPeak' = 'SUPER PEAK',
}

export const getPeakTypeLabelText = (
  peakType: PeakType | null,
  lowerCase = false
) => {
  const peakText =
    peakType === PeakType.PEAK
      ? PeakTypeMapping.peak
      : peakType === PeakType.SUPER_PEAK
      ? PeakTypeMapping.superPeak
      : PeakTypeMapping.offPeak;

  return lowerCase ? peakText.toLowerCase() : peakText;
};

export const getBreakDaysTypeLabelText = (
  breakDaysType: BreakDaysType | null
) => {
  return breakDaysType?.toUpperCase();
};

export const getBreakTypesLabelText = (
  startDate: string,
  endDate: string,
  allPeakDatesData: PeakDatesData[]
) => {
  startDate = DateTime.fromISO(startDate).toFormat('yyyy-MM-dd');
  endDate = DateTime.fromISO(endDate).toFormat('yyyy-MM-dd');

  const breakDaysType = getBreakDaysType(startDate, endDate);

  const peakType = getBreakPeakType(startDate, endDate, allPeakDatesData);

  const peakText = getPeakTypeLabelText(peakType);

  const dayTypeText = getBreakDaysTypeLabelText(breakDaysType);

  return `${peakText} (${dayTypeText})`;
};

export const getBreakLabel = (
  startDate: string,
  endDate: string,
  breakVisitType: BreakVisitType,
  allPeakDatesData: PeakDatesData[],
  isLet2Own = false
) => {
  if (
    isLet2Own &&
    [BreakVisitType.LET_WITH_HAVEN, BreakVisitType.WAITLIST].includes(
      breakVisitType
    )
  ) {
    return getBreakTypesLabelText(startDate, endDate, allPeakDatesData);
  } else if (breakVisitType === BreakVisitType.MAINTENANCE) {
    return '—';
  } else {
    const duration =
      Interval.fromDateTimes(
        DateTime.fromISO(startDate),
        DateTime.fromISO(endDate)
      ).count('days') - 1 || 0;

    return `${duration} NIGHT${duration > 1 ? 'S' : ''}`;
  }
};

export const getBreakTransitions = async (
  accountId: number,
  code: number | undefined,
  startDate: string,
  outOfSeason: string,
  { rethrowError = false }: { rethrowError?: boolean } = {}
) => {
  try {
    const transitions = await fetchWrapper(
      withApiBaseUrl(
        `/plot/${accountId}/lettingbreaks/status?status=${code}&startDate=${startDate}&outOfSeason=${outOfSeason}`
      ),
      {
        method: 'GET',
        credentials: 'include',
      }
    );
    return transitions as BreakTransition[];
  } catch (error) {
    console.error(error);
    // Callers that pass rethrowError need to tell a failed request apart from a
    // break with no available transitions; the default swallow keeps existing
    // callers unchanged.
    if (rethrowError) {
      throw error;
    }
    return [];
  }
};

export const getBreakDataForSelection = async (
  accountId: number,
  selection: InfoSelection
) => {
  const { startDate, endDate } = selection.dates;

  const breaksQuery = new URLSearchParams();
  breaksQuery.append('startDate', startDate.toFormat('yyyy-MM-dd'));
  breaksQuery.append('endDate', endDate.toFormat('yyyy-MM-dd'));
  breaksQuery.append('includeOverlap', 'true');

  try {
    return await fetchWrapper(
      withApiBaseUrl(
        `/plot/${accountId}/lettingbreaks?${breaksQuery.toString()}`
      ),
      {
        method: 'GET',
        credentials: 'include',
      }
    );
  } catch (error) {
    console.error(error);
  }
};

export const evaluateHavenLetsInSelection = (
  selection: InfoSelection,
  type?: BookingType
) => {
  const evaluation = {
    overlapWithHavenLet: false,
    withinSixWeeks: false,
    overlapWithPexHandover: false,
  };

  const havenLetsInSelection = selection.events?.filter((event) =>
    isLetWithHaven(event.code)
  );

  if (!havenLetsInSelection?.length) {
    return evaluation;
  }

  evaluation.overlapWithPexHandover = havenLetsInSelection.some((havenLet) =>
    isUnavailable(havenLet.data.status)
  );

  const mappedHavenLets = havenLetsInSelection.map((havenLet) => {
    const startDateTime = DateTime.fromISO(havenLet?.data?.startDate);
    const endDateTime = DateTime.fromISO(havenLet?.data?.endDate);

    return {
      start: startDateTime.plus({ days: 1 }).toMillis(),
      end: endDateTime.minus({ days: 1 }).toMillis(),
    };
  });

  evaluation.withinSixWeeks =
    havenLetsInSelection[0]?.data?.status?.withinSixWeeks;
  const { startDate, endDate } = selection?.dates || {};

  const bypassOverlapCheck =
    type === BookingType.OWNER && startDate === endDate;

  evaluation.overlapWithHavenLet =
    !bypassOverlapCheck &&
    mappedHavenLets.some(
      (havenLet) =>
        startDate?.toMillis() <= havenLet.end &&
        havenLet.start <= endDate?.toMillis()
    );

  return evaluation;
};

export const sortBookingDataByArrivalDate = (arrivalData: EventData<any>[]) =>
  arrivalData.sort(
    (a, b) =>
      DateTime.fromISO(a.data.startDate || a.data.dateArrival).toMillis() -
      DateTime.fromISO(b.data.startDate || b.data.dateArrival).toMillis()
  );

export const getGuestSelectorOptions = ({
  fandfCards,
  activeAccount,
}: {
  fandfCards: OwnersCard[] | null;
  activeAccount: UserAccount | null;
}) => {
  if (!fandfCards) {
    return [
      {
        label: 'Friends & Family Card Holder',
        value: 'privilege_card_holder',
      },
    ];
  }
  return activeAccount?.isCharity || fandfCards.length === 0
    ? []
    : [
        {
          label: 'Friends & Family Card Holder',
          options: fandfCards.map((card: OwnersCard) => ({
            label: `${card.firstName} ${card.lastName}`,
            value: card.id,
          })),
        },
      ];
};

export const sanitizeName = (name: string) => {
  return name.replace(/[`‘’´`]/g, "'");
};
